import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Container, Card, Badge, Button } from 'react-bootstrap';
import { SearchBar } from '@core/components/SearchBar';
import { useDebounce } from '@core/hooks/useDebounce';
import { useSearchUrlSync } from '@core/hooks/useSearchUrlSync';
import { useLocation, useSearchParams } from 'react-router-dom';
import { DataTable, DataTableColumn } from '@core/components/DataTable';
import { useNavigate, useParams } from 'react-router-dom';
import { useAssignedTemplates } from '../hooks/useAssignedTemplates';
import AssignNationReportTemplatesModal from '../components/AssignNationReportTemplatesModal';
import { DepartmentWithTemplates } from '../types/assignedTemplates';
import { FiFileText } from 'react-icons/fi';
import { usePermissions } from '@features/entity/hooks/usePermissions';
import { ACTION_CODES, getScopedTemplateSyncCode } from '@features/entity/types/actionCodes';
import RequiredBadge from '@core/components/RequiredBadge';
import { buildActivityEntrySubmitSearch } from '@features/report/activity-entry/constants/activityEntryQueryParams';

type ScopeType = 'CHURCH' | 'DISTRICT' | 'NATION';

export const AssignedTemplatesPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { churchId, districtId, nationId } = useParams<{
    churchId?: string;
    districtId?: string;
    nationId?: string;
  }>();

  const { scope, scopeId, basePath } = useMemo((): { scope: ScopeType; scopeId: number; basePath: string } => {
    if (churchId) return { scope: 'CHURCH', scopeId: Number(churchId) || 0, basePath: `/church/${churchId}` };
    if (districtId) return { scope: 'DISTRICT', scopeId: Number(districtId) || 0, basePath: `/district/${districtId}` };
    if (nationId) return { scope: 'NATION', scopeId: Number(nationId) || 0, basePath: `/nation/${nationId}` };
    return { scope: 'CHURCH', scopeId: 0, basePath: '/church/0' };
  }, [churchId, districtId, nationId]);

  const { hasPermission } = usePermissions({
    scopeType: scope,
    scopeId,
    actionCodes: [getScopedTemplateSyncCode(scope)],
  });
  const canManageTemplates =
    scope === 'NATION' && hasPermission(ACTION_CODES.SYNC_NATION_REPORT_TEMPLATE);
  const [showManageModal, setShowManageModal] = useState(false);

  const [urlParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(() => urlParams.get('q') ?? '');
  const debouncedQuery = useDebounce(searchQuery, 300);
  const previousPathnameRef = useRef(location.pathname);

  useSearchUrlSync({ query: debouncedQuery, filters: {}, page: 1 });

  const { departmentsWithTemplates, loading, refetch } = useAssignedTemplates({
    churchId: scope === 'CHURCH' ? scopeId : undefined,
    districtId: scope === 'DISTRICT' ? scopeId : undefined,
    nationId: scope === 'NATION' ? scopeId : undefined,
    freetext: debouncedQuery || undefined,
  });

  useEffect(() => {
    if (previousPathnameRef.current === location.pathname) return;

    previousPathnameRef.current = location.pathname;
    setSearchQuery('');
  }, [location.pathname]);

  const handleTemplateClick = (departmentId: number, templateId: number) => {
    navigate(
      `${basePath}/reports/submit?${buildActivityEntrySubmitSearch({
        reportTemplateId: templateId,
        departmentId,
      }).toString()}`
    );
  };

  const columns: DataTableColumn<DepartmentWithTemplates>[] = [
    {
      key: 'department_name',
      label: 'Department',
      role: 'title',
      cardRender: (_value: any, row: DepartmentWithTemplates) => row.department_name,
      render: (_value: any, row: DepartmentWithTemplates) => (
        <div>
          <span className="fw-medium">{row.department_name}</span>
          <div className="mt-1">
            <Badge bg="light" className="fw-normal border border-light-subtle text-primary">{row.templates.length} template{row.templates.length !== 1 ? 's' : ''}</Badge>
          </div>
        </div>
      ),
    },
    {
      key: 'templates',
      label: 'Report Templates',
      cardLabel: '',
      render: (_value: any, row: DepartmentWithTemplates) => (
        <div>
          {row.templates.map((template, index) => (
            <div
              key={template.report_template_id}
              role="button"
              tabIndex={0}
              className={`py-2 px-2 rounded ntcog-clickable-row ${index > 0 ? 'border-top' : ''}`}
              onClick={() => handleTemplateClick(row.department_id, template.report_template_id)}
              onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleTemplateClick(row.department_id, template.report_template_id); } }}
            >
              <div className="d-flex align-items-center gap-2">
                <div className="fw-medium text-primary">{template.report_template_name}</div>
                {template.is_required && (
                  <RequiredBadge title="This report is required" aria-label="Required report" />
                )}
              </div>
              {template.report_template_description && (
                <small className="text-muted">{template.report_template_description}</small>
              )}
            </div>
          ))}
          {row.templates.length === 0 && (
            <span className="text-muted">No templates assigned</span>
          )}
        </div>
      ),
    },
  ];

  const scopeLabels: Record<ScopeType, string> = {
    CHURCH: 'this church',
    DISTRICT: 'this district',
    NATION: 'this nation',
  };

  const scopeTemplateLabels: Record<ScopeType, string> = {
    CHURCH: 'church',
    DISTRICT: 'district',
    NATION: 'nation',
  };

  const titleLabels: Record<ScopeType, string> = {
    CHURCH: 'Church Templates',
    DISTRICT: 'District Templates',
    NATION: 'Nation Templates',
  };

  const noTemplatesMessage = `No ${scopeTemplateLabels[scope]} templates available yet.`;

  return (
    <Container fluid className="py-4">
      <div className="d-flex flex-column flex-md-row justify-content-md-between align-items-md-center gap-3 mb-4">
        <div className="ntcog-page-title-block mb-0">
          <h1 className="ntcog-page-heading mb-1">{titleLabels[scope]}</h1>
          <p className="text-muted mb-0">Report templates assigned to {scopeLabels[scope]}.</p>
        </div>
        {canManageTemplates && (
          <Button
            variant="primary"
            onClick={() => setShowManageModal(true)}
            aria-label="Manage Templates"
          >
            <FiFileText className="me-2" />
            Manage Templates
          </Button>
        )}
      </div>

      <div className="ntcog-list-search-section">
        <SearchBar
          placeholder="Search templates…"
          initialQuery={searchQuery}
          onQueryChange={setSearchQuery}
          onFiltersApply={() => {}}
          onClear={() => setSearchQuery('')}
          loading={loading}
          testId="assigned-templates-search"
        />
      </div>

      <Card className="shadow-sm ntcog-card ntcog-list-table-card">
        <Card.Body className="p-0">
          <DataTable
            data={departmentsWithTemplates}
            columns={columns}
            loading={loading}
            error={null}
            emptyMessage={noTemplatesMessage}
            cardAriaLabel={(row) => `Department ${row.department_name}`}
            striped={false}
            hover={false}
            responsive
          />
        </Card.Body>
      </Card>

      {scope === 'NATION' && (
        <AssignNationReportTemplatesModal
          show={showManageModal}
          onHide={() => setShowManageModal(false)}
          nationId={scopeId}
          onSaved={() => refetch()}
        />
      )}
    </Container>
  );
};
